import {
  Component,
  Input,
  HostBinding,
  EventEmitter,
  Output,
  TemplateRef
} from '@angular/core';
import { NG_VALUE_ACCESSOR, ControlValueAccessor } from '@angular/forms';
import { NumberChange } from './number-change.class';

@Component({
  selector: 'suka-number',
  template: `
    <label *ngIf="skeleton && label" class="label skeleton"></label>
    <label *ngIf="!skeleton && label" [for]="id" class="label">
      <ng-container *ngIf="!isTemplate(label)">{{label}}</ng-container>
      <ng-template *ngIf="isTemplate(label)" [ngTemplateOutlet]="label"></ng-template>
    </label>
    <div *ngIf="!skeleton && helperText" class="form__helper-text">
      <ng-container *ngIf="!isTemplate(helperText)">{{helperText}}</ng-container>
      <ng-template *ngIf="isTemplate(helperText)" [ngTemplateOutlet]="helperText"></ng-template>
    </div>
    <div
      data-numberinput
      [attr.data-invalid]="(invalid ? true : null)"
      [attr.data-valid]="(valid ? true : null)"
      class="number-input"
      [ngClass]="{
        'number-input--nolabel': !label,
        'number-input--helpertext': helperText,
        'skeleton' : skeleton
      }">
      <div class="number-input__input-wrapper">
        <input
          type="number"
          [id]="id"
          [value]="value"
          [attr.min]="min"
          [attr.max]="max"
          [disabled]="disabled"
          [required]="required"
          (input)="onNumberInputChange($event)"/>
        <suka-icon *ngIf="!skeleton && invalid" icon="alert-circle" class="number-input__invalid"></suka-icon>
        <suka-icon *ngIf="!skeleton && valid" icon="check" class="number-input__valid"></suka-icon>
        <div *ngIf="!skeleton" class="number-input__controls">
          <button
            class="number-input__control-btn up-icon"
            type="button"
            aria-live="polite"
            aria-atomic="true"
            (click)="onIncrement()">
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 320 512" height="16" width="16"><path fill="currentColor" d="M288.662 352H31.338c-17.818 0-26.741-21.543-14.142-34.142l128.662-128.662c7.81-7.81 20.474-7.81 28.284 0l128.662 128.662c12.6 12.599 3.676 34.142-14.142 34.142z"></path></svg>
          </button>
          <button
            class="number-input__control-btn down-icon"
            type="button"
            aria-live="polite"
            aria-atomic="true"
            (click)="onDecrement()">
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 320 512" height="16" width="16"><path fill="currentColor" d="M31.3 192h257.3c17.8 0 26.7 21.5 14.1 34.1L174.1 354.8c-7.8 7.8-20.5 7.8-28.3 0L17.2 226.1C4.6 213.5 13.5 192 31.3 192z"></path></svg>
          </button>
        </div>
      </div>
      <div *ngIf="invalid" class="form-requirement">
        <ng-container *ngIf="!isTemplate(invalidText)">{{invalidText}}</ng-container>
        <ng-template *ngIf="isTemplate(invalidText)" [ngTemplateOutlet]="invalidText"></ng-template>
      </div>
    </div>
  `,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: Number,
      multi: true
    }
  ]
})
// tslint:disable-next-line: component-class-suffix
export class Number implements ControlValueAccessor {
  /**
   * Variable used for creating unique ids for number input components.
   */
  static numberCount = 0;

  @HostBinding('class.form-item') containerClass = true;
  /**
   * Set to `true` for a disabled number input.
   */
  @Input() disabled = false;
  /**
   * Set to `true` for a loading number component.
   */
  @Input() skeleton = false;
  /**
   * Set to `true` for an invalid number component.
   */
  @Input() invalid = false;
  /**
   * Set to `true` for a valid number component.
   */
  @Input() valid = false;
  /**
   * The unique id for the number component.
   */
  @Input() id = `number-input-${Number.numberCount}`;
  /**
   * Reflects the required attribute of the `input` element.
   */
  @Input() required: boolean;
  /**
   * Sets the value attribute on the `input` element.
   */
  @Input() value = 0;
  /**
   * Sets the min attribute on the `input` element.
   */
  @Input() min = null;
  /**
   * Sets the max attribute on the `input` element.
   */
  @Input() max = null;
  /**
   * Sets the text inside the `label` tag.
   */
  @Input() label: string | TemplateRef<any>;
  /**
   * Sets the optional helper text.
   */
  @Input() helperText: string | TemplateRef<any>;
  /**
   * Sets the invalid text.
   */
  @Input() invalidText: string | TemplateRef<any>;
  /**
   * Emits event notifying other classes when a change in state occurs in the input.
   */
  // tslint:disable-next-line: no-output-native
  @Output() change = new EventEmitter<NumberChange>();

  /**
   * Creates an instance of `Number`.
   */
  constructor() {
    Number.numberCount++;
  }

  /**
   * This is the initial value set to the component
   * @param value The input value.
   */
  public writeValue(value: any) {
    this.value = value;
  }

  /**
   * Sets a method in order to propagate changes back to the form.
   */
  public registerOnChange(fn: any) {
    this.propagateChange = fn;
  }

  /**
   * Registers a callback to be triggered when the control has been touched.
   * @param fn Callback to be triggered when the number input is touched.
   */
  public registerOnTouched(fn: any) {
    this.onTouched = fn;
  }

  /**
   * Sets the disabled state through the model
   */
  setDisabledState(isDisabled: boolean) {
    this.disabled = isDisabled;
  }

  /**
   * Called when number input is blurred. Needed to properly implement `ControlValueAccessor`.
   */
  onTouched: () => any = () => { };

  /**
   * Method set in `registerOnChange` to propagate changes back to the form.
   */
  propagateChange = (_: any) => { };

  /**
   * Adds 1 to the current `value`.
   */
  onIncrement(): void {
    if (this.max === null || this.value < this.max) {
      this.value++;
      this.emitChangeEvent();
    }
  }

  /**
   * Subtracts 1 to the current `value`.
   */
  onDecrement(): void {
    if (this.min === null || this.value > this.min) {
      this.value--;
      this.emitChangeEvent();
    }
  }

  /**
   * Creates a class of `NumberChange` to emit the change in the `Number`.
   */
  emitChangeEvent(): void {
    const event = new NumberChange();
    event.source = this;
    event.value = this.value;
    this.change.emit(event);
    this.propagateChange(this.value);
  }

  onNumberInputChange(event) {
    this.value = event.target.value;
    this.emitChangeEvent();
  }

  public isTemplate(value) {
    return value instanceof TemplateRef;
  }
}
